@if (isOpen) {
  <div class="settings-backdrop" (click)="discardAndClose()"></div>
}
@if (isOpen) {
  <div class="dashboard-settings-flyout" (click)="$event.stopPropagation()">
    <div class="flyout-header">
      <h3 class="flyout-title">
        @if (!editingTitle) {
          {{ dashboardView?.viewName || ('Dashboard Settings' | translate) }}
          <button type="button" class="edit-title-btn" (click)="startEditingTitle()" [title]="'Rename' | translate">
            <i class="material-icons">edit</i>
          </button>
        }
        @if (editingTitle) {
          <input #titleInput [(ngModel)]="viewNameEdit" (blur)="saveViewName()"
            (keydown.enter)="saveViewName()" (keydown.escape)="cancelEditingTitle()" class="edit-title-input" autofocus />
        }
        @if (hasChanges) {
          <span class="temporary-view-tag">{{ 'Temporary View' | translate }}</span>
        }
      </h3>
      <button type="button" class="close-btn" (click)="discardAndClose()" [title]="'Close' | translate">
        <i class="material-icons">close</i>
      </button>
    </div>
    <div class="flyout-tabs">
      <button type="button" class="tab-btn" [class.active]="currentTab === 'widgets'" (click)="setTab('widgets')">
        <i class="material-icons">widgets</i>
        <span>{{ 'Widgets' | translate }}</span>
      </button>
      <button type="button" class="tab-btn" [class.active]="currentTab === 'layout'" (click)="setTab('layout')">
        <i class="material-icons">grid_view</i>
        <span>{{ 'Layout' | translate }}</span>
      </button>
      <button type="button" class="tab-btn" [class.active]="currentTab === 'properties'" (click)="setTab('properties')"
        [disabled]="!selectedWidget">
        <i class="material-icons">tune</i>
        <span>{{ 'Properties' | translate }}</span>
      </button>
    </div>
    <div class="flyout-content">
      <!-- Widgets Tab -->
      @if (currentTab === 'widgets') {
        <div class="tab-panel">
          <div class="section">
            <div class="section-header-with-action">
              <div>
                <h4 class="section-title">{{ 'Available Widgets' | translate }}</h4>
                <p class="section-description">{{ 'Toggle widgets on or off. Click + to add duplicate widgets.' | translate
                }}</p>
              </div>
              <div class="section-header-buttons">
                <button type="button" class="reset-default-btn" (click)="onResetToDefault()"
                  [title]="'Reset to Default' | translate">
                  <i class="material-icons">restore</i>
                  <span>{{ 'Reset to Default' | translate }}</span>
                </button>
                <button type="button" class="add-widget-btn" (click)="openWidgetPickerDialog()"
                  [title]="'Add Widget' | translate">
                  <i class="material-icons">add</i>
                  <span>{{ 'Add Widget' | translate }}</span>
                </button>
              </div>
            </div>
            <!-- Widget Toggle List with dynamic layout class -->
            <div class="widget-toggle-list" [ngClass]="getWidgetListLayoutClass()">
              @for (widget of widgetInstances; track trackByWidgetId($index, widget)) {
                <div class="widget-toggle-item"
                  [class.enabled]="widget.visible">
                  <div class="widget-toggle-icon"
                    [matTooltip]="widgetDescriptionByWidgetId[widget.id] | translate"
                    matTooltipPosition="right">
                    <i class="material-icons">{{ widgetIconByWidgetId[widget.id] }}</i>
                  </div>
                  <div class="widget-toggle-info">
                    <span class="widget-toggle-name">{{ widgetTitleMetaByWidgetId[widget.id]?.isCustom ? widgetDisplayNameByWidgetId[widget.id] : (widgetDisplayNameByWidgetId[widget.id] | translate) }}{{ widgetTitleMetaByWidgetId[widget.id]?.tailPrefix }}{{ widgetTitleMetaByWidgetId[widget.id]?.tailKey | translate }}{{ widgetTitleMetaByWidgetId[widget.id]?.tail }}</span>
                  </div>
                  <div class="widget-toggle-actions">
                    @if (widget.visible) {
                      <button type="button" class="config-btn" (click)="selectWidgetForConfig(widget)"
                        [title]="'Configure' | translate">
                        <i class="material-icons">settings</i>
                      </button>
                    }
                    <button type="button" class="delete-btn" (click)="removeWidget(widget.id)" [title]="'Delete' | translate">
                      <i class="material-icons">delete</i>
                    </button>
                    <label class="toggle-switch">
                      <input type="checkbox" [checked]="widget.visible" (change)="onWidgetToggle(widget, $event)" />
                      <span class="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      }
      <!-- Layout Tab -->
      @if (currentTab === 'layout') {
        <div class="tab-panel">
          <div class="section">
            <h4 class="section-title">{{ 'Grid Settings' | translate }}</h4>
            <div class="form-group">
              <label>{{ 'Columns' | translate }}</label>
              <select [ngModel]="layoutConfig.columns" (ngModelChange)="onColumnChange($event)">
                @for (opt of columnOptions; track trackByIndex($index)) {
                  <option [ngValue]="opt.value">{{ opt.label | translate }}</option>
                }
              </select>
              <span class="form-hint">{{ 'Number of columns in the grid' | translate }}</span>
            </div>
            <div class="form-group">
              <label>{{ 'Row Height' | translate }}</label>
              <select [ngModel]="getSelectedRowHeightPreset()" (ngModelChange)="onRowHeightPresetChange($event)">
                @for (preset of rowHeightPresets; track trackByPresetKey($index, preset)) {
                  <option [value]="preset.key">{{ preset.label | translate }}</option>
                }
              </select>
              <span class="form-hint">{{ 'Size of each widget row' | translate }}</span>
            </div>
            <div class="form-group">
              <label>{{ 'Spacing' | translate }}</label>
              <select [ngModel]="getSelectedGapPreset()" (ngModelChange)="onGapPresetChange($event)">
                @for (preset of gapPresets; track trackByPresetKey($index, preset)) {
                  <option [value]="preset.key">{{ preset.label | translate }}</option>
                }
              </select>
              <span class="form-hint">{{ 'Space between widgets' | translate }}</span>
            </div>
          </div>
          <div class="section">
            <h4 class="section-title">{{ 'Preview' | translate }}</h4>
            <p class="section-description">{{ 'Shows current widget layout. Widgets can span multiple cells.' | translate }}
            </p>
            <div class="layout-preview" [style.--columns]="layoutConfig.columns" [style.--gap]="getPreviewGap()"
              [style.--row-height]="getPreviewRowHeight()">
              @for (cell of previewCells; track trackByCellIndex($index); let i = $index) {
                @if (!previewSpanOccupiedMap[i]) {
                  <div class="preview-cell" [class.has-widget]="previewWidgetMap[i]"
                    [style.grid-column]="previewWidgetMap[i] ? 'span ' + previewWidgetMap[i]!.width : null"
                    [style.grid-row]="previewWidgetMap[i] ? 'span ' + previewWidgetMap[i]!.height : null">
                    @if (previewWidgetMap[i]; as widget) {
                      <i class="material-icons">{{ widgetIconByWidgetId[widget.id] }}</i>
                      <span class="preview-widget-name">{{ widgetTitleMetaByWidgetId[widget.id]?.isCustom ? widgetDisplayNameByWidgetId[widget.id] : (widgetDisplayNameByWidgetId[widget.id] | translate) }}{{ widgetTitleMetaByWidgetId[widget.id]?.tailPrefix }}{{ widgetTitleMetaByWidgetId[widget.id]?.tailKey | translate }}{{ widgetTitleMetaByWidgetId[widget.id]?.tail }}</span>
                      <span class="preview-widget-size">{{ widget.width }}×{{ widget.height }}</span>
                    }
                  </div>
                }
              }
            </div>
          </div>
        </div>
      }
      <!-- Properties Tab -->
      @if (currentTab === 'properties') {
        <div class="tab-panel">
          @if (!selectedWidget) {
            <div class="no-selection">
              <i class="material-icons">touch_app</i>
              <span>{{ 'Select a widget to configure' | translate }}</span>
            </div>
          }
          @if (selectedWidget && selectedWidgetDefinition) {
            <div class="section">
              <h4 class="section-title">{{ selectedWidgetDefinition.name | translate }}</h4>
              <div class="form-group">
                <label>{{ 'Title' | translate }}</label>
                <input type="text" [(ngModel)]="selectedWidget.title"
                  (change)="onWidgetTitleChange(selectedWidget, selectedWidget.title || '')"
                  [placeholder]="selectedWidgetDefinition.name | translate" />
                  <span class="form-hint">{{ 'Custom title for this widget (optional)' | translate }}</span>
                </div>
              </div>
              <div class="section">
                <h4 class="section-title">{{ 'Size & Position' | translate }}</h4>
                @if (selectedWidgetDefinition) {
                  <p class="section-description">
                    {{ 'Min size:' | translate }} {{ selectedWidgetDefinition.minWidth }}×{{ selectedWidgetDefinition.minHeight
                    }},
                    {{ 'Max size:' | translate }} {{ selectedWidgetDefinition.maxWidth || layoutConfig.columns }}×{{
                    selectedWidgetDefinition.maxHeight || '∞' }}
                  </p>
                }
                <div class="size-presets">
                  @for (preset of sizePresets; track trackByPresetKey($index, preset)) {
                    <button type="button" class="preset-btn"
                      [class.active]="selectedWidget.width === preset.width && selectedWidget.height === preset.height"
                      [disabled]="!isSizePresetValid(preset)"
                      [title]="!isSizePresetValid(preset) ? ('Size ' + preset.width + '×' + preset.height + ' not allowed for this widget' | translate) : ''"
                    (click)="applySizePreset(preset.key)">{{ preset.label | translate }}</button>
                  }
                </div>
                <div class="position-controls">
                  <div class="control-group">
                    <label>{{ 'Width' | translate }}</label>
                    <div class="stepper">
                      <button type="button" (click)="updateWidgetSize(selectedWidget, 'width', -1)"
                      [disabled]="!canUpdateWidgetSize(selectedWidget, 'width', -1)">−</button>
                      <span>{{ selectedWidget.width }}</span>
                      <button type="button" (click)="updateWidgetSize(selectedWidget, 'width', 1)"
                      [disabled]="!canUpdateWidgetSize(selectedWidget, 'width', 1)">+</button>
                    </div>
                  </div>
                  <div class="control-group">
                    <label>{{ 'Height' | translate }}</label>
                    <div class="stepper">
                      <button type="button" (click)="updateWidgetSize(selectedWidget, 'height', -1)"
                      [disabled]="!canUpdateWidgetSize(selectedWidget, 'height', -1)">−</button>
                      <span>{{ selectedWidget.height }}</span>
                      <button type="button" (click)="updateWidgetSize(selectedWidget, 'height', 1)"
                      [disabled]="!canUpdateWidgetSize(selectedWidget, 'height', 1)">+</button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="section" [style.display]="selectedWidgetDefinition?.configComponent ? '' : 'none'">
                <h4 class="section-title">{{ 'Widget Settings' | translate }}</h4>
                <div #configHost></div>
                <div class="form-group">
                  <label>{{ 'Organization Level' | translate }}</label>
                  <app-widget-org-filter [(orgLevelId)]="widgetOrgLevelOverrideId" [fullWidth]="true"></app-widget-org-filter>
                </div>
              </div>
              @if (!selectedWidgetDefinition?.configComponent && selectedWidgetDefinition?.configSchema?.length) {
                <div class="section">
                  <h4 class="section-title">{{ 'Widget Settings' | translate }}</h4>
                  @for (field of selectedWidgetDefinition.configSchema; track trackByFieldKey($index, field)) {
                    @if (shouldShowConfigField(field)) {
                      <div class="form-group">
                        <label>{{ field.label | translate }}</label>
                        @if (field.type === 'text') {
                          <input type="text" [ngModel]="widgetConfig[field.key]"
                            (ngModelChange)="onConfigFieldChange(field, $event)" />
                        }
                        @if (field.type === 'number') {
                          <input type="number" [ngModel]="widgetConfig[field.key]"
                            (ngModelChange)="onConfigFieldChange(field, $event)" [min]="$safeNavigationMigration(field.validation?.min)"
                            [max]="$safeNavigationMigration(field.validation?.max)" />
                        }
                        @if (field.type === 'select') {
                          <mat-form-field appearance="outline" class="multi-select-field">
                            <mat-select [ngModel]="widgetConfig[field.key]" (ngModelChange)="onConfigFieldChange(field, $event)">
                              @for (opt of field.options; track trackByIndex($index)) {
                                <mat-option [value]="opt.value">{{ opt.label | translate }}</mat-option>
                              }
                            </mat-select>
                          </mat-form-field>
                        }
                        @if (field.type === 'multi-select') {
                          <mat-form-field appearance="outline" class="multi-select-field">
                            <mat-select [ngModel]="widgetConfig[field.key] || []" (ngModelChange)="onConfigFieldChange(field, $event)" multiple>
                              @for (opt of field.options; track trackByIndex($index)) {
                                <mat-option [value]="opt.value">{{ opt.label | translate }}</mat-option>
                              }
                            </mat-select>
                          </mat-form-field>
                        }
                        @if (field.type === 'boolean') {
                          <label class="toggle-label">
                            <input type="checkbox" [ngModel]="widgetConfig[field.key]"
                              (ngModelChange)="onConfigFieldChange(field, $event)" />
                              <span class="toggle-slider"></span>
                            </label>
                          }
                          @if (field.type === 'color') {
                            <input type="color" [ngModel]="widgetConfig[field.key]"
                              (ngModelChange)="onConfigFieldChange(field, $event)" />
                          }
                          @if (field.helpText) {
                            <span class="form-hint">{{ field.helpText | translate }}</span>
                          }
                        </div>
                      }
                    }
                    <div class="form-group">
                      <label>{{ 'Organization Level' | translate }}</label>
                      <app-widget-org-filter [(orgLevelId)]="widgetOrgLevelOverrideId" [fullWidth]="true"></app-widget-org-filter>
                    </div>
                  </div>
                }
                @if (!selectedWidgetDefinition?.configComponent && !selectedWidgetDefinition?.configSchema?.length) {
                  <div class="section">
                    <h4 class="section-title">{{ 'Widget Settings' | translate }}</h4>
                    <div class="form-group">
                      <label>{{ 'Organization Level' | translate }}</label>
                      <app-widget-org-filter [(orgLevelId)]="widgetOrgLevelOverrideId" [fullWidth]="true"></app-widget-org-filter>
                    </div>
                  </div>
                }
              }
            </div>
          }
        </div>
        <div class="share-view-section" (click)="toggleShareView()">
          <mat-checkbox [(ngModel)]="shareView" (change)="onShareToggle($event.checked)" (click)="$event.stopPropagation()">
            {{ 'Share View' | translate }}
          </mat-checkbox>
        </div>
        @if (shareView && organizationLevels.length > 0) {
          <div class="org-level-select-section">
            <label class="org-level-label">{{ 'Visibility' | translate }}</label>
            <mat-form-field appearance="outline" class="org-level-select">
              <mat-select [(ngModel)]="selectedOrgLevelId" (selectionChange)="onOrgLevelChange()"
                panelClass="hierarchical-select-panel">
                <ng-container
                *ngTemplateOutlet="renderOrgTree; context: { nodes: organizationLevelsHierarchical, level: 0 }"></ng-container>
                <ng-template #renderOrgTree let-nodes="nodes" let-level="level">
                  @for (node of nodes; track trackById($index, node)) {
                    <mat-option [value]="node.id" [ngClass]="'mat-level-' + level">
                      @for (i of [].constructor(level); track trackByIndex($index); let idx = $index) {
                        <div class="pre-option-line" [ngClass]="'pre-option-level-' + idx"></div>
                      }
                      {{ node.name }}
                    </mat-option>
                    @if (node.children?.length) {
                      <ng-container
                      *ngTemplateOutlet="renderOrgTree; context: { nodes: node.children, level: level + 1 }"></ng-container>
                    }
                  }
                </ng-template>
              </mat-select>
            </mat-form-field>
          </div>
        }
        <div class="flyout-footer">
          <button type="button" class="btn btn-secondary" (click)="discardAndClose()">{{ 'Cancel' | translate }}</button>
          <button type="button" class="btn btn-primary" (click)="saveAndClose()">{{ 'Save Changes' |
          translate }}</button>
        </div>
      </div>
    }
